/**
 * FNP-1378 — RESOLVING A CONNECTION ID TO WHAT THE TENANT CALLED IT.
 *
 * ⚠️ A LOOKUP, NOT A FIELD ON THE ZONE. `DnsZone` carries `connectionId`, an
 * opaque `PROVIDER_CONNECTION#<tenant>#<uuid>` that means nothing to a reader.
 * The tenant's own `displayName` lives on the connection, and the zones page
 * already loads the connections for the Add button, so the name is resolved
 * from that fetch rather than pushed into the zone shape.
 *
 * ⚠️ ITS OWN MODULE, extracted from `useZonesTableColumns` when the grouped
 * rows gave a second consumer (`ZoneConnections`). A hook file that also
 * exports a plain function breaks Fast Refresh, and this app lints at
 * --max-warnings 0.
 */

/** Connection id → the tenant's own name for it. */
export type ZoneConnectionNames = ReadonlyMap<string, string>;

/**
 * The last segment of a connection id, for the moment before names arrive.
 *
 * ⚠️ NOT THE WHOLE ID. `PROVIDER_CONNECTION#<tenant-uuid>#<connection-uuid>` is
 * 80 characters and its first two thirds are identical on every row of a
 * tenant's table, so rendering it whole would push the table wide to show a
 * prefix that distinguishes nothing. The tail is the only part that differs.
 *
 * Shown dimmed by its callers, because it is a placeholder for a name rather
 * than a value a tenant should ever need to read.
 */
export const shortConnectionId = (connectionId: string): string => {
    const tail = connectionId.split('#').pop() ?? connectionId;
    return tail.length > 8 ? tail.slice(0, 8) : tail;
};
